import {
  Inject,
  Injectable,
  InjectionToken,
  Injector,
  OnDestroy,
  Optional,
} from '@angular/core'
import { Store } from '@ngrx/store'
import { Actions, ofType } from '@ngrx/effects'
import { Subscription } from 'rxjs'
import { AuthTokenService } from '@helios/auth'
import {
  BULK_COUNTS_MAX_ACCOUNTS,
  RootState,
  commentaryAccountCountsLoaded,
  commentaryAccountsUnavailable,
  commentaryCountsLoadFailed,
  loadCommentaryActivity,
} from '@helios/state-management'
import {
  DENIAL_RETRY_DELAYS_MS,
  THREADS_WS_PATH,
  ThreadEventSocketPool,
  ThreadEventSocketPoolDeps,
  denialRetryDelay,
} from '../live/thread-event-socket-pool'
import { announceLiveThreadChanges } from '../live/live-thread-changes'
import { CommentaryCountsService } from './commentary-counts.service'

/**
 * What an owner shows for its accounts, which decides what an event reloads:
 * - `entity`: per-item badges (entity grids, the session) → loadCommentaryCounts
 * - `total`: per-account totals (account grids) → loadCommentaryAccountCounts
 * - `drawer`: the drawer's account → loadCommentaryActivity (header badge)
 */
export type CommentaryLiveSurface = 'entity' | 'total' | 'drawer'

/** Identity key of a registry owner (a grid's `destroy$`, a sentinel object).
 *  Compared by reference only, never inspected. */
// eslint-disable-next-line @typescript-eslint/ban-types
export type CommentaryLiveOwner = object

export type ThreadEventSocketFactory = (
  url: string,
  protocols: string[],
) => WebSocket

/** DLITE-5094: how the thread-event socket is constructed; tests swap a fake. */
export const THREAD_EVENT_SOCKET_FACTORY =
  new InjectionToken<ThreadEventSocketFactory>('THREAD_EVENT_SOCKET_FACTORY', {
    providedIn: 'root',
    factory: () => (url: string, protocols: string[]) =>
      new WebSocket(url, protocols),
  })

/** DLITE-5094: absolute ws(s) URL of commentary-ws's thread-event socket. */
export const THREAD_EVENT_SOCKET_URL = new InjectionToken<string>(
  'THREAD_EVENT_SOCKET_URL',
  {
    providedIn: 'root',
    factory: () => {
      const loc = typeof window !== 'undefined' ? window.location : null
      const scheme = loc?.protocol === 'http:' ? 'ws' : 'wss'
      return `${scheme}://${loc?.host ?? ''}${THREADS_WS_PATH}`
    },
  },
)

/** Registry churn (grid scroll bursts) is diffed against the socket after this. */
export const LIVE_REGISTRY_DEBOUNCE_MS = 100
/** Events for one account inside this window collapse into one reload. */
export const LIVE_EVENT_COALESCE_MS = 300

type PoolLike = Pick<ThreadEventSocketPool, 'setAccounts' | 'stop' | 'isLive'>

/** DLITE-5094: constructs the pool; a token so specs can stub it. */
export const THREAD_EVENT_SOCKET_POOL_FACTORY = new InjectionToken<
  (deps: ThreadEventSocketPoolDeps) => PoolLike
>('THREAD_EVENT_SOCKET_POOL_FACTORY', {
  providedIn: 'root',
  factory: () => (deps: ThreadEventSocketPoolDeps) =>
    new ThreadEventSocketPool(deps),
})

interface OwnerEntry {
  surface: CommentaryLiveSurface
  ids: Set<string>
}

/**
 * DLITE-5094: keeps commentary badges live. Owners (each grid instance, the
 * session, the drawer) register the accounts they display; the union is kept
 * subscribed on commentary-ws's thread-event socket, and a thread change in
 * any of them reloads it through the existing counts/activity actions.
 *
 * Nothing connects until `start()`, which AppComponent calls only for users
 * with commentary access; registrations made before that are held and
 * applied on start.
 */
@Injectable({ providedIn: 'root' })
export class CommentaryLiveAccountsService implements OnDestroy {
  private readonly owners = new Map<CommentaryLiveOwner, OwnerEntry>()
  private pool: PoolLike | null = null
  private syncTimer: ReturnType<typeof setTimeout> | null = null
  private reloadTimer: ReturnType<typeof setTimeout> | null = null
  private readonly pendingReloads = new Map<
    string,
    Set<CommentaryLiveSurface> | null
  >()
  /** Surfaces per account as of the last sync, to spot surfaces leaving. */
  private syncedSurfaces = new Map<string, Set<CommentaryLiveSurface>>()
  /** Bulk-`failed` accounts awaiting a retry → retries already made. */
  private readonly bulkRetry = new Map<string, number>()
  /** Of those, the ids whose latest load failed and is not yet retried. */
  private readonly bulkRetryDue = new Set<string>()
  private bulkRetryTimer: ReturnType<typeof setTimeout> | null = null
  private actionsSub: Subscription | null = null

  constructor(
    private readonly store: Store<RootState>,
    // AuthTokenService is not root-provided (AuthModule provides it), so it is
    // resolved lazily in start(): constructing this service — e.g. in a grid's
    // TestBed — must not require the auth module.
    private readonly injector: Injector,
    @Inject(THREAD_EVENT_SOCKET_FACTORY)
    private readonly socketFactory: ThreadEventSocketFactory,
    @Inject(THREAD_EVENT_SOCKET_URL) private readonly url: string,
    @Inject(THREAD_EVENT_SOCKET_POOL_FACTORY)
    private readonly poolFactory: (deps: ThreadEventSocketPoolDeps) => PoolLike,
    private readonly counts: CommentaryCountsService,
    // Optional, as in CommentaryCountsService: tests need no EffectsModule.
    @Optional() @Inject(Actions) private readonly actions$: Actions | null,
  ) {}

  get started(): boolean {
    return this.pool !== null
  }

  /** True when a change to `accountId` will arrive over the socket (the
   *  connection holding it is open and the server confirmed it). False
   *  before start(). Callers use it to skip a local refresh the socket
   *  event will do anyway. */
  isLive(accountId: string | number | null | undefined): boolean {
    if (!this.pool || accountId === null || accountId === undefined) {
      return false
    }
    const id = String(accountId)
    return id !== '' && this.pool.isLive(id)
  }

  start(): void {
    if (this.pool) return
    const auth = this.injector.get(AuthTokenService)
    this.pool = this.poolFactory({
      url: this.url,
      getToken: (forceRefresh?: boolean) => auth.getToken(forceRefresh),
      createSocket: this.socketFactory,
      maxPerConnection: BULK_COUNTS_MAX_ACCOUNTS,
      callbacks: {
        onEvent: ids => this.onThreadChanges(ids),
        // The only reconnect reload path, so a reconnect reloads once.
        onReconnected: ids => this.onThreadChanges(ids),
        // Admission does not reload: the grids' own ensure* calls (and
        // setCommentaryAccount for the drawer) are the single first-display
        // fetch. The exception is recovery: an account marked unavailable
        // that the server now admits is reloaded, and that load's success
        // clears the mark.
        onAdmitted: ids => this.recoverAdmitted(ids),
        onDenied: ids => this.markDenied(ids),
      },
    })
    this.actionsSub =
      this.actions$
        ?.pipe(
          ofType(commentaryCountsLoadFailed, commentaryAccountCountsLoaded),
        )
        .subscribe(action => this.onCountsResult(action)) ?? null
    if (this.owners.size > 0) this.sync()
  }

  stop(): void {
    this.clearTimers()
    this.pendingReloads.clear()
    this.bulkRetry.clear()
    this.bulkRetryDue.clear()
    this.actionsSub?.unsubscribe()
    this.actionsSub = null
    this.syncedSurfaces = new Map()
    this.pool?.stop()
    this.pool = null
  }

  ngOnDestroy(): void {
    this.stop()
  }

  /** Replace what `owner` displays. `owner` is an identity key only. */
  setOwner(
    owner: CommentaryLiveOwner,
    surface: CommentaryLiveSurface,
    ids: (string | number)[],
  ): void {
    const next = new Set<string>()
    for (const id of ids) {
      if (id !== null && id !== undefined && id !== '') next.add(String(id))
    }
    this.owners.set(owner, { surface, ids: next })
    this.scheduleSync()
  }

  releaseOwner(owner: CommentaryLiveOwner): void {
    if (this.owners.delete(owner)) this.scheduleSync()
  }

  private union(): Set<string> {
    const all = new Set<string>()
    this.owners.forEach(entry => entry.ids.forEach(id => all.add(id)))
    return all
  }

  private surfacesByAccount(): Map<string, Set<CommentaryLiveSurface>> {
    const byAccount = new Map<string, Set<CommentaryLiveSurface>>()
    this.owners.forEach(entry =>
      entry.ids.forEach(id => {
        const surfaces = byAccount.get(id) ?? new Set<CommentaryLiveSurface>()
        surfaces.add(entry.surface)
        byAccount.set(id, surfaces)
      }),
    )
    return byAccount
  }

  private scheduleSync(): void {
    if (!this.pool || this.syncTimer) return
    this.syncTimer = setTimeout(() => {
      this.syncTimer = null
      this.sync()
    }, LIVE_REGISTRY_DEBOUNCE_MS)
  }

  private sync(): void {
    if (!this.pool) return
    const current = this.surfacesByAccount()
    // Stale-on-leave: once no surface of a kind shows an account, the socket's
    // reloads skip that kind, so its cached counts may go out of date. Mark it
    // stale; the grid's own ensure* call then fetches it once when it is shown
    // again. (An owner released and re-added inside one debounce window never
    // left: the subscription was continuous, and flushReloads marks any
    // surface it skips.) The drawer needs nothing: setCommentaryAccount
    // always reloads activity.
    this.syncedSurfaces.forEach((before, id) => {
      const now = current.get(id)
      before.forEach(surface => {
        if (now?.has(surface)) return
        this.markStale(id, surface)
      })
    })
    this.syncedSurfaces = current
    // A bulk retry is only for an account still shown on a total surface.
    ;[...this.bulkRetry.keys()].forEach(id => {
      if (!current.get(id)?.has('total')) this.forgetBulkRetry(id)
    })
    // The pool diffs against what it already holds, so an account still held
    // by another owner is never unsubscribed.
    this.pool.setAccounts([...current.keys()])
  }

  private markStale(id: string, surface: CommentaryLiveSurface): void {
    if (surface === 'drawer') return
    this.counts.markStale(id, surface)
  }

  /** A live invalidation (thread event or reconnect): reload the badges and,
   *  for server-side grids filtering on Comments, announce it so they
   *  re-query which rows match. Only displayed accounts are announced. */
  private onThreadChanges(ids: string[]): void {
    if (!this.pool) return
    this.queueReloads(ids)
    const registered = this.union()
    announceLiveThreadChanges(ids.filter(id => registered.has(id)))
  }

  /** `surfaces` null = every surface currently showing the account. */
  private queueReloads(
    ids: string[],
    surfaces: Set<CommentaryLiveSurface> | null = null,
  ): void {
    if (!this.pool) return
    const registered = this.union()
    let added = false
    for (const id of ids) {
      if (!registered.has(id)) continue
      added = true
      const existing = this.pendingReloads.get(id)
      if (surfaces === null || existing === null) {
        this.pendingReloads.set(id, null)
      } else {
        const merged = new Set(existing ?? [])
        surfaces.forEach(s => merged.add(s))
        this.pendingReloads.set(id, merged)
      }
    }
    if (!added || this.reloadTimer) return
    this.reloadTimer = setTimeout(() => {
      this.reloadTimer = null
      this.flushReloads()
    }, LIVE_EVENT_COALESCE_MS)
  }

  /** True when the event batch already holds a forced reload of `id` that
   *  covers `surface` (null entry = every showing surface). */
  private reloadQueued(id: string, surface: CommentaryLiveSurface): boolean {
    if (!this.pendingReloads.has(id)) return false
    const wanted = this.pendingReloads.get(id)
    return wanted === null || !!wanted?.has(surface)
  }

  private flushReloads(): void {
    const pending = [...this.pendingReloads]
    this.pendingReloads.clear()
    const current = this.surfacesByAccount()
    const totals: string[] = []
    for (const [id, wanted] of pending) {
      const showing = current.get(id) ?? new Set<CommentaryLiveSurface>()
      // A surface not showing the account now cannot be reloaded, but its
      // cache just went out of date: mark it so the next display fetches once.
      for (const kind of ['entity', 'total'] as const) {
        if (!showing.has(kind) && (!wanted || wanted.has(kind))) {
          this.markStale(id, kind)
        }
      }
      if (showing.size === 0) continue
      const surfaces = wanted
        ? new Set([...showing].filter(s => wanted.has(s)))
        : showing
      // Forced (an event or reconnect may postdate any load already running),
      // but recorded in the counts service's in-flight markers, so a recovery
      // or grid load while this request runs is not sent again.
      if (surfaces.has('entity')) this.counts.forceReload([id], 'entity')
      if (surfaces.has('total')) totals.push(id)
      // The activity slice tracks one account and re-points on load, so only
      // the drawer's own account may trigger it.
      if (surfaces.has('drawer')) {
        this.store.dispatch(loadCommentaryActivity({ accountId: id }))
      }
    }
    if (totals.length > 0) this.counts.forceReload(totals, 'total')
  }

  private markDenied(ids: string[]): void {
    // An expected outcome, not an error: no console output, no error log.
    if (ids.length === 0) return
    this.store.dispatch(commentaryAccountsUnavailable({ accountIds: ids }))
  }

  /** Admitted ids that are marked unavailable get one reload of their showing
   *  surfaces; its success clears the mark in the reducer. Other admissions
   *  reload nothing. Counts go through CommentaryCountsService's dedup, at
   *  once rather than through the event batch: a grid load already running
   *  is not repeated, and one that has already succeeded cleared the mark,
   *  so this skips the account. Only events and reconnects force-load.
   *
   *  A surface that already has a forced reload queued for the account (a
   *  reconnect reply — which the pool reports through onReconnected before
   *  onAdmitted — or a pending thread event) is left to that reload, which
   *  clears the mark on success, so the account is loaded once per surface.
   *  Once that batch has been sent, its requests are in the counts service's
   *  in-flight markers (forceReload), as is a load from the drawer fallback,
   *  so refreshUnlessLoading skips them until their result arrives. */
  private recoverAdmitted(ids: string[]): void {
    const marked = ids.filter(id => this.counts.isUnavailable(id))
    if (marked.length === 0) return
    const showing = this.surfacesByAccount()
    const bySurface = (kind: CommentaryLiveSurface) =>
      marked.filter(
        id => showing.get(id)?.has(kind) && !this.reloadQueued(id, kind),
      )
    const entity = bySurface('entity')
    const total = bySurface('total')
    if (entity.length > 0) this.counts.refreshUnlessLoading(entity, 'entity')
    if (total.length > 0) this.counts.refreshUnlessLoading(total, 'total')
    // A kind not showing the account fetches once when it is shown again.
    for (const kind of ['entity', 'total'] as const) {
      marked
        .filter(id => !showing.get(id)?.has(kind))
        .forEach(id => this.markStale(id, kind))
    }
    // Activity has no dedup helper; the drawer's account reloads it once.
    const drawer = bySurface('drawer')
    if (drawer.length > 0) this.queueReloads(drawer, new Set(['drawer']))
  }

  private onCountsResult(
    action:
      | ReturnType<typeof commentaryCountsLoadFailed>
      | ReturnType<typeof commentaryAccountCountsLoaded>,
  ): void {
    if (action.type === commentaryAccountCountsLoaded.type) {
      const loaded = (
        action as ReturnType<typeof commentaryAccountCountsLoaded>
      ).payload.counts
      Object.keys(loaded ?? {}).forEach(id => this.forgetBulkRetry(id))
      return
    }
    const { accountId, retryable, source } = (
      action as ReturnType<typeof commentaryCountsLoadFailed>
    ).payload
    // A failed entity GET says nothing about the account's bulk totals.
    if (source === 'single') return
    if (!retryable) {
      // A real denial (403) or a fault: not something a retry here fixes.
      this.forgetBulkRetry(accountId)
      return
    }
    if (!this.surfacesByAccount().get(accountId)?.has('total')) return
    const made = this.bulkRetry.get(accountId) ?? 0
    if (made >= DENIAL_RETRY_DELAYS_MS.length) return
    this.bulkRetry.set(accountId, made)
    this.bulkRetryDue.add(accountId)
    this.scheduleBulkRetry()
  }

  private scheduleBulkRetry(): void {
    if (this.bulkRetryTimer || !this.pool) return
    const made = [...this.bulkRetryDue].map(id => this.bulkRetry.get(id) ?? 0)
    if (made.length === 0) return
    this.bulkRetryTimer = setTimeout(() => {
      this.bulkRetryTimer = null
      this.retryBulkFailed()
    }, denialRetryDelay(Math.min(...made)))
  }

  private retryBulkFailed(): void {
    const showing = this.surfacesByAccount()
    const ids: string[] = []
    this.bulkRetryDue.forEach(id => {
      if (!showing.get(id)?.has('total')) return
      this.bulkRetry.set(id, (this.bulkRetry.get(id) ?? 0) + 1)
      ids.push(id)
    })
    this.bulkRetryDue.clear()
    // Through the dedup: the failure already dropped the requested marker, so
    // this is one POST, and a grid load that got there first is not repeated.
    if (ids.length > 0) this.counts.ensureAccountsLoaded(ids)
  }

  private forgetBulkRetry(id: string): void {
    this.bulkRetry.delete(id)
    this.bulkRetryDue.delete(id)
  }

  private clearTimers(): void {
    if (this.syncTimer) clearTimeout(this.syncTimer)
    if (this.reloadTimer) clearTimeout(this.reloadTimer)
    if (this.bulkRetryTimer) clearTimeout(this.bulkRetryTimer)
    this.syncTimer = null
    this.reloadTimer = null
    this.bulkRetryTimer = null
  }
}
